<template>
  <div class="min-h-screen bg-background text-foreground">
    <header class="sticky top-0 z-30 border-b bg-card/95 backdrop-blur md:hidden">
      <div class="flex items-center justify-between px-4 py-3">
        <h1 class="text-base font-semibold uppercase tracking-wide">{{ msg || 'Админ-панель' }}</h1>
        <div class="flex items-center gap-2">
          <button
            class="button button-outline !h-8 !w-8 !px-0"
            title="Переключить тему"
            @click="toggleTheme"
          >
            <svg v-if="!isDark" class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM4.343 5.757a1 1 0 001.414 1.414l.707-.707A1 1 0 105.05 5.05l-.707.707zM5.757 14.243a1 1 0 00-1.414 1.414l.707.707a1 1 0 001.414-1.414l-.707-.707zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          </button>
          <button class="button button-outline !h-8 !w-8 !px-0" @click="toggleMobileMenu">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <div class="flex min-h-screen flex-col md:flex-row">
      <aside v-if="mobileMenuOpen" class="border-b bg-card px-4 py-4 md:hidden">
        <button class="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm font-semibold" @click="toggleMenu">
          <span>{{ userName }}</span>
          <span>▼</span>
        </button>

        <div v-if="menuVisible" class="mt-2 rounded-md border bg-popover p-1 text-popover-foreground shadow-sm">
          <button class="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted" @click="editProfile">Изменить</button>
          <button class="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted" @click="logout">Выйти</button>
        </div>

        <div class="mt-4 space-y-1">
          <button
            v-if="canAccessAdminSections"
            :class="navItemClass(currentView === 'ApplicationsView')"
            @click="selectView('applications')"
          >
            Заявки
          </button>
          <button :class="navItemClass(currentView === 'ProjectView')" @click="selectView('project')">{{ projectsMenuLabel }}</button>
          <button :class="navItemClass(currentView === 'BlogView')" @click="selectView('blog')">{{ presentationsMenuLabel }}</button>
          <button :class="navItemClass(currentView === 'FilesView')" @click="selectView('files')">Файлы</button>
          <button :class="navItemClass(currentView === 'VideosView')" @click="selectView('videos')">Видео</button>
          <button
            v-if="canAccessAdminSections"
            :class="navItemClass(currentView === 'UsersView')"
            @click="selectView('users')"
          >
            Пользователи
          </button>
          <button
            v-if="canAccessAdminSections"
            :class="navItemClass(currentView === 'BackstageGalleryManager')"
            @click="selectView('backstage-gallery')"
          >
            Backstage галереи
          </button>
          <button
            v-if="canEditMainCommercial"
            :class="navItemClass(isCommercialModeActive('main'))"
            @click="selectView('commercial-main')"
          >
            Редактор КП (Сайт)
          </button>
          <button
            :class="navItemClass(isCommercialModeActive('user'))"
            @click="openCurrentUserCommercialEditor"
          >
            {{ commercialUserMenuLabel }}
          </button>
          <button
            v-if="canAccessSystemSettings"
            :class="navItemClass(currentView === 'SystemSettingsView')"
            @click="selectView('system-settings')"
          >
            Настройки системы
          </button>
        </div>
      </aside>

      <aside class="hidden w-72 shrink-0 border-r bg-card md:sticky md:top-0 md:flex md:h-screen md:flex-col md:self-start md:overflow-y-auto">
        <div class="flex h-full flex-col gap-5 p-5">
          <div>
            <h1 class="text-xl font-bold uppercase tracking-wide">{{ msg || 'Админ-панель' }}</h1>
          </div>

          <div class="relative">
            <button class="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-left text-sm font-semibold" @click="toggleMenu">
              <span class="truncate">{{ userName }}</span>
              <span>▼</span>
            </button>

            <div
              v-if="menuVisible"
              class="absolute left-0 top-full z-50 mt-2 w-full rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
              @click.stop
            >
              <button class="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted" @click.stop="editProfile">Изменить</button>
              <button class="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted" @click.stop="inviteUser">Пригласить</button>
              <button class="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted" @click.stop="logout">Выйти</button>
            </div>
          </div>

          <button
            class="button button-outline w-full justify-between"
            title="Переключить тему"
            @click="toggleTheme"
          >
            <span>Тема: {{ isDark ? 'Темная' : 'Светлая' }}</span>
            <svg v-if="!isDark" class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM4.343 5.757a1 1 0 001.414 1.414l.707-.707A1 1 0 105.05 5.05l-.707.707zM5.757 14.243a1 1 0 00-1.414 1.414l.707.707a1 1 0 001.414-1.414l-.707-.707zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          </button>

          <nav class="space-y-1">
            <button
              v-if="canAccessAdminSections"
              :class="navItemClass(currentView === 'ApplicationsView')"
              @click="selectView('applications')"
            >
              Заявки
            </button>
            <button :class="navItemClass(currentView === 'ProjectView')" @click="selectView('project')">{{ projectsMenuLabel }}</button>
            <button :class="navItemClass(currentView === 'BlogView')" @click="selectView('blog')">{{ presentationsMenuLabel }}</button>
            <button :class="navItemClass(currentView === 'FilesView')" @click="selectView('files')">Файлы</button>
            <button :class="navItemClass(currentView === 'VideosView')" @click="selectView('videos')">Видео</button>
            <button
              v-if="canAccessAdminSections"
              :class="navItemClass(currentView === 'UsersView')"
              @click="selectView('users')"
            >
              Пользователи
            </button>
            <button
              v-if="canAccessAdminSections"
              :class="navItemClass(currentView === 'BackstageGalleryManager')"
              @click="selectView('backstage-gallery')"
            >
              Backstage галереи
            </button>
            <button
              v-if="canEditMainCommercial"
              :class="navItemClass(isCommercialModeActive('main'))"
              @click="selectView('commercial-main')"
            >
              Редактор КП (Сайт)
            </button>
            <button
              :class="navItemClass(isCommercialModeActive('user'))"
              @click="openCurrentUserCommercialEditor"
            >
              {{ commercialUserMenuLabel }}
            </button>
            <button
              v-if="canAccessSystemSettings"
              :class="navItemClass(currentView === 'SystemSettingsView')"
              @click="selectView('system-settings')"
            >
              Настройки системы
            </button>
          </nav>
        </div>
      </aside>

      <main class="min-w-0 flex-1 p-4 md:p-6">
        <transition-view name="fade" :duration="250" :force-key="`${currentView}-${commercialEditorMode}`">
          <component
            :is="currentView"
            :key="`${currentView}-${commercialEditorMode}`"
            v-bind="currentViewProps"
            @commercial-filled-updated="onCommercialFilledUpdated"
            @edit-commercial="openForeignCommercialEditor"
          />
        </transition-view>
      </main>
    </div>

    <UserModal
      :isVisible="isModalVisible && currentUser && currentUser.id"
      :user="currentUser"
      :isEditMode="true"
      :isSuperuser="currentUser.is_superuser || false"
      @submit="updateUser"
      @close="closeModal"
    />

    <div
      v-if="isInviteModalVisible"
      class="fixed inset-0 z-[1200] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
      @click="closeInviteModal"
    >
      <div class="w-full max-w-xl rounded-xl border bg-card p-5 text-card-foreground shadow-2xl" @click.stop>
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 class="text-lg font-semibold">Ссылка приглашения</h3>
            <p class="mt-1 text-sm text-muted-foreground">Эта ссылка действительна 24 часа</p>
          </div>
          <button
            class="button button-outline !h-8 !w-8 !px-0"
            aria-label="Закрыть"
            @click="closeInviteModal"
          >
            ✕
          </button>
        </div>

        <div class="mb-4 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            :value="invitationLink"
            readonly
            class="input-field flex-1"
          />
          <button class="button whitespace-nowrap" @click="copyInvitationLink">Копировать</button>
        </div>

        <div class="flex justify-end">
          <button class="button button-outline" @click="closeInviteModal">Закрыть</button>
        </div>
      </div>
    </div>

    <div
      v-if="isRoleOverrideModalVisible"
      class="fixed inset-0 z-[1250] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
      @click="closeRoleOverrideModal"
    >
      <div class="w-full max-w-lg rounded-xl border bg-card p-5 text-card-foreground shadow-2xl" @click.stop>
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 class="text-lg font-semibold">Локальный режим прав</h3>
            <p class="mt-1 text-sm text-muted-foreground">
              Изменения применяются только в браузере и не сохраняются в БД.
            </p>
          </div>
          <button
            class="button button-outline !h-8 !w-8 !px-0"
            aria-label="Закрыть"
            @click="closeRoleOverrideModal"
          >
            ✕
          </button>
        </div>

        <div class="space-y-3">
          <label class="flex items-center justify-between rounded-md border bg-background px-3 py-2">
            <span class="text-sm font-medium">Суперюзер (локально)</span>
            <input v-model="roleOverrideForm.is_superuser" type="checkbox" class="h-4 w-4 accent-[hsl(var(--accent))]" />
          </label>

          <label class="flex items-center justify-between rounded-md border bg-background px-3 py-2">
            <span class="text-sm font-medium">Админ (локально)</span>
            <input v-model="roleOverrideForm.is_admin" type="checkbox" class="h-4 w-4 accent-[hsl(var(--accent))]" />
          </label>
        </div>

        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <button class="button button-outline" :disabled="!hasStoredRoleOverride" @click="resetRoleOverride">
            Сбросить
          </button>
          <button class="button button-outline" @click="closeRoleOverrideModal">Отмена</button>
          <button class="button" @click="applyRoleOverride">Применить</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProjectView from './ProjectView.vue';
import BlogView from './ProjectsV2/BlogView.vue';
import UsersView from './UsersView.vue';
import FilesView from './FilesView.vue';
import VideosView from './VideosView.vue';
import ApplicationsView from './ApplicationsView.vue';
import BackstageGalleryManager from './BackstageGalleryManager.vue';
import CommercialView from './CommercialView.vue';
import SystemSettingsView from './SystemSettingsView.vue';
import UserModal from './UserModal.vue';
import TransitionView from './TransitionView.vue';
import { fetchUserData, updateUserApi, createInvitationApi } from '../services/api';
import { useToast } from 'vue-toastification';
import { useTheme } from '../utils/useTheme';
import { computed } from 'vue';
import {
  HESOYAM_KEY_CODES,
  applyUserRoleOverride,
  isUserAdminForUi,
  getUserRoleOverride,
  setUserRoleOverride,
  clearUserRoleOverride,
  getRoleOverrideEventName
} from '../utils/user-role-override';

export default {
  name: 'DashboardPage',
  props: {
    msg: String
  },
  components: {
    ProjectView,
    BlogView,
    UsersView,
    FilesView,
    VideosView,
    ApplicationsView,
    BackstageGalleryManager,
    CommercialView,
    SystemSettingsView,
    UserModal,
    TransitionView
  },
  setup() {
    const toast = useToast();
    const { currentTheme, toggleTheme: toggleThemeFunc } = useTheme();
    const isDark = computed(() => currentTheme.value === 'dark');
    
    return { 
      toast, 
      isDark,
      toggleTheme: toggleThemeFunc
    };
  },
  data() {
    return {
      currentView: 'ApplicationsView',
      commercialEditorMode: 'all',
      commercialEditorTargetUser: null,
      userName: '',
      menuVisible: false,
      mobileMenuOpen: false,
      isModalVisible: false,
      currentUser: {},
      isInviteModalVisible: false,
      invitationLink: '',
      isRoleOverrideModalVisible: false,
      roleOverrideForm: {
        is_superuser: false,
        is_admin: false
      },
      secretCodeBuffer: []
    };
  },
  computed: {
    canAccessAdminSections() {
      return Boolean(this.currentUser?.is_superuser) || isUserAdminForUi(this.currentUser);
    },
    canEditMainCommercial() {
      return this.canAccessAdminSections;
    },
    canAccessSystemSettings() {
      return Boolean(this.currentUser?.is_superuser);
    },
    projectsMenuLabel() {
      return this.canAccessAdminSections ? 'Проекты' : 'Мои проекты';
    },
    presentationsMenuLabel() {
      return this.canAccessAdminSections ? 'Презентации' : 'Мои презентации';
    },
    commercialUserMenuLabel() {
      const targetUser = this.currentCommercialEditorUser;
      const username = targetUser?.username || 'username';
      return `Редактор КП (${username})`;
    },
    currentCommercialEditorUser() {
      if (
        this.commercialEditorTargetUser &&
        this.canAccessAdminSections &&
        this.commercialEditorTargetUser.id
      ) {
        return this.commercialEditorTargetUser;
      }
      return this.currentUser || {};
    },
    hasStoredRoleOverride() {
      return Boolean(getUserRoleOverride());
    },
    currentViewProps() {
      if (this.currentView === 'CommercialView') {
        const editorUser = this.currentCommercialEditorUser;
        return {
          mode: this.commercialEditorMode,
          currentUserId: editorUser?.id || '',
          currentUsername: editorUser?.username || '',
          currentCommercialFilled: Boolean(editorUser?.commercial_filled),
        };
      }
      if (
        this.currentView === 'UsersView' ||
        this.currentView === 'ProjectView' ||
        this.currentView === 'BlogView' ||
        this.currentView === 'FilesView' ||
        this.currentView === 'VideosView'
      ) {
        return {
          currentUser: this.currentUser,
        };
      }
      return {};
    },
  },
  created() {
    this.fetchUser();
    const view = this.$route.params.view || 'applications';
    this.applyView(view);
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeyDown);
    window.addEventListener(getRoleOverrideEventName(), this.handleRoleOverrideChanged);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeyDown);
    window.removeEventListener(getRoleOverrideEventName(), this.handleRoleOverrideChanged);
  },
  methods: {
    isRestrictedViewForRegularUser(view) {
      return ['applications', 'users', 'backstage-gallery', 'system-settings'].includes(view);
    },
    resolveAccessibleView(view) {
      const normalizedView = String(view || 'project');
      // До загрузки пользователя не ограничиваем маршрут, чтобы не ломать deep-link.
      if (!this.currentUser?.id) {
        if (normalizedView === 'commercial') {
          return this.canEditMainCommercial ? 'commercial-main' : 'commercial-user';
        }
        if (normalizedView === 'commercial-main' && !this.canEditMainCommercial) {
          return 'commercial-user';
        }
        return normalizedView;
      }

      if (this.isRestrictedViewForRegularUser(normalizedView) && !this.canAccessAdminSections) {
        return 'project';
      }
      if (normalizedView === 'commercial') {
        return this.canEditMainCommercial ? 'commercial-main' : 'commercial-user';
      }
      if (normalizedView === 'commercial-main' && !this.canEditMainCommercial) {
        return 'commercial-user';
      }
      if (normalizedView === 'system-settings' && !this.canAccessSystemSettings) {
        return 'project';
      }
      return normalizedView;
    },
    handleGlobalKeyDown(event) {
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const keyCode = event.code || '';
      if (!keyCode.startsWith('Key')) {
        this.secretCodeBuffer = [];
        return;
      }

      this.secretCodeBuffer.push(keyCode);
      if (this.secretCodeBuffer.length > HESOYAM_KEY_CODES.length) {
        this.secretCodeBuffer = this.secretCodeBuffer.slice(-HESOYAM_KEY_CODES.length);
      }

      const isCheatCodeMatched = HESOYAM_KEY_CODES.every((code, index) => this.secretCodeBuffer[index] === code);
      if (!isCheatCodeMatched) {
        return;
      }

      this.secretCodeBuffer = [];
      this.openRoleOverrideModal();
    },
    handleRoleOverrideChanged() {
      this.applyCurrentUserRoleOverride();
    },
    openRoleOverrideModal() {
      const savedOverride = getUserRoleOverride();
      const user = this.currentUser || {};
      this.roleOverrideForm = {
        is_superuser: savedOverride ? savedOverride.is_superuser : Boolean(user.is_superuser),
        is_admin: savedOverride ? savedOverride.is_admin : Boolean(isUserAdminForUi(user))
      };
      this.isRoleOverrideModalVisible = true;
    },
    closeRoleOverrideModal() {
      this.isRoleOverrideModalVisible = false;
    },
    applyRoleOverride() {
      setUserRoleOverride({
        is_superuser: Boolean(this.roleOverrideForm.is_superuser),
        is_admin: Boolean(this.roleOverrideForm.is_admin)
      });
      this.applyCurrentUserRoleOverride();
      this.isRoleOverrideModalVisible = false;
      this.toast.success('Локальные права применены');
    },
    resetRoleOverride() {
      clearUserRoleOverride();
      this.applyCurrentUserRoleOverride();
      this.isRoleOverrideModalVisible = false;
      this.toast.success('Локальные права сброшены');
    },
    applyCurrentUserRoleOverride() {
      if (!this.currentUser || !this.currentUser.id) {
        return;
      }
      this.currentUser = applyUserRoleOverride(this.currentUser);
      this.userName = this.currentUser.title || this.currentUser.username;

      if (!this.canEditMainCommercial && this.isCommercialModeActive('main')) {
        this.selectView('commercial-user');
        return;
      }

      if (
        this.commercialEditorTargetUser &&
        !this.canAccessAdminSections
      ) {
        this.commercialEditorTargetUser = null;
      }

      if (this.currentView === 'SystemSettingsView' && !this.canAccessSystemSettings) {
        this.selectView('project');
        return;
      }

      const routeView = String(this.$route.params.view || '');
      if (!routeView && !this.canAccessAdminSections && this.currentView === 'ApplicationsView') {
        this.selectView('project');
        return;
      }
      if (routeView && this.isRestrictedViewForRegularUser(routeView) && !this.canAccessAdminSections) {
        this.selectView('project');
      }
    },
    navItemClass(isActive) {
      return [
        'w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
        isActive ? 'bg-accent text-accent-foreground shadow-sm' : 'text-foreground hover:bg-muted'
      ];
    },
    applyView(view) {
      const resolvedView = this.resolveAccessibleView(view);
      if (resolvedView === 'backstage-gallery') {
        this.currentView = 'BackstageGalleryManager';
        this.commercialEditorMode = 'all';
      } else if (resolvedView === 'system-settings') {
        this.currentView = 'SystemSettingsView';
        this.commercialEditorMode = 'all';
      } else if (resolvedView === 'commercial-main') {
        this.currentView = 'CommercialView';
        this.commercialEditorMode = 'main';
      } else if (resolvedView === 'commercial-user') {
        this.currentView = 'CommercialView';
        this.commercialEditorMode = 'user';
      } else {
        this.currentView = resolvedView.charAt(0).toUpperCase() + resolvedView.slice(1) + 'View';
        this.commercialEditorMode = 'all';
      }
    },
    isCommercialModeActive(mode) {
      return this.currentView === 'CommercialView' && this.commercialEditorMode === mode;
    },
    async fetchUser() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        const userData = await fetchUserData();
        this.currentUser = applyUserRoleOverride(userData);
        this.userName = this.currentUser.title || this.currentUser.username;

        const routeView = this.$route.params.view;
        if (!routeView && !this.canAccessAdminSections) {
          this.selectView('project');
          return;
        }
        if (routeView && this.isRestrictedViewForRegularUser(routeView) && !this.canAccessAdminSections) {
          this.selectView('project');
          return;
        }
        if (routeView === 'system-settings' && !this.canAccessSystemSettings) {
          this.selectView('project');
          return;
        }
        if (routeView === 'commercial-main' && !this.canEditMainCommercial) {
          this.selectView('commercial-user');
          return;
        }
        if (routeView === 'commercial') {
          this.selectView(this.canEditMainCommercial ? 'commercial-main' : 'commercial-user');
          return;
        }
        if (routeView === 'commercial-main' || routeView === 'commercial-user') {
          this.applyView(routeView);
        }
      } catch (error) {
        console.error('Ошибка при получении данных пользователя:', error);
        this.toast.error('Ошибка при получении данных пользователя');
        if (error.message.includes('Authentication required')) {
          this.$router.push('/login');
        }
      }
    },
    selectView(view) {
      this.commercialEditorTargetUser = null;
      const targetView = this.resolveAccessibleView(view);
      this.applyView(targetView);
      this.$router.push({ path: `/${targetView}` });
      this.mobileMenuOpen = false; // Закрываем мобильное меню при выборе вкладки
    },
    openCurrentUserCommercialEditor() {
      this.commercialEditorTargetUser = null;
      this.selectView('commercial-user');
    },
    openForeignCommercialEditor(user) {
      if (!this.canAccessAdminSections || !user?.id) {
        return;
      }
      if (String(user.id) === String(this.currentUser?.id || '')) {
        this.openCurrentUserCommercialEditor();
        return;
      }
      this.commercialEditorTargetUser = {
        id: user.id,
        username: user.username,
        commercial_filled: Boolean(user.commercial_filled)
      };
      this.applyView('commercial-user');
      this.$router.push({ path: '/commercial-user' });
      this.mobileMenuOpen = false;
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      this.toast.success('Вы успешно вышли из системы');
      this.$router.push('/login');
    },
    async inviteUser() {
      this.menuVisible = false;
      try {
        const response = await createInvitationApi();
        this.invitationLink = response.invitation_url;
        this.isInviteModalVisible = true;
      } catch (error) {
        console.error('Ошибка при создании приглашения:', error);
        this.toast.error('Ошибка при создании приглашения');
      }
    },
    closeInviteModal() {
      this.isInviteModalVisible = false;
      this.invitationLink = '';
    },
    copyInvitationLink() {
      navigator.clipboard.writeText(this.invitationLink);
      this.toast.success('Ссылка скопирована!');
    },
    toggleMenu() {
      this.menuVisible = !this.menuVisible;
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    editProfile(event) {
      event.preventDefault();
      event.stopPropagation();
      
      if (!this.currentUser || !this.currentUser.id) {
        this.toast.error('Ошибка: данные пользователя не загружены');
        return;
      }
      
      this.isModalVisible = true;
      this.menuVisible = false;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    async updateUser(updatedData) {
      try {
        // Отправляем обновленные данные на сервер
        await updateUserApi(this.currentUser.id, updatedData);

        // Обновляем локальные данные
        if (updatedData.title) {
          this.userName = updatedData.title;
        }
        this.currentUser = { ...this.currentUser, ...updatedData };

        this.toast.success('Профиль успешно обновлен');
        this.closeModal();

        // Перезагружаем данные пользователя
        await this.fetchUser();
      } catch (error) {
        console.error('Ошибка при обновлении пользователя:', error);
        this.toast.error('Ошибка при обновлении профиля: ' + (error.response?.data?.detail || error.message));
      }
    },
    navigateToBackstageGallery() {
      this.selectView('backstage-gallery');
    },
    onCommercialFilledUpdated(value) {
      const nextValue = Boolean(value);
      if (this.commercialEditorTargetUser) {
        this.commercialEditorTargetUser = {
          ...this.commercialEditorTargetUser,
          commercial_filled: nextValue,
        };
        if (String(this.commercialEditorTargetUser.id) !== String(this.currentUser?.id || '')) {
          return;
        }
      }

      this.currentUser = {
        ...this.currentUser,
        commercial_filled: nextValue,
      };
    }
  }
}
</script>
